import React, { useEffect, useState, useMemo } from "react";
import {
  IconButton,
  InputAdornment,
  MenuItem,
  Select,
} from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import SupportStyleTable from "../../Components/Table/SupportStyleTable";
import { fetchMembers } from "../../Redux/Slice/Homepage";
import { addSubTaskMember, ShowAllSubTask } from "../../Redux/Slice/SubTask";
import { fetchActiveMemberData } from "../../Redux/Slice/TaskMasterSlice";
import Loader from "../../Components/Loader/Loader";
import "../CustomerReq/InhouseRnd.css";
import {
  Layers,
  User,
  PlusCircle,
  Pencil,
  X,
  ChevronDown,
} from "lucide-react";

const SubTask = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { ActiveMemberData } = useSelector((state) => state.TaskMaster);
  const { AllSubTaskData, loading } = useSelector((state) => state.SubTask);

  const [subTaskName, setSubTaskName] = useState("");
  const [personName, setPersonName] = useState("");

  useEffect(() => {
    dispatch(ShowAllSubTask());
    dispatch(fetchMembers());
    dispatch(fetchActiveMemberData());
  }, [dispatch]);

  const handlePage = (id) => {
    navigate(`/subtask/editsubtask/${id}`);
  };

  const handleTaskSubmit = async () => {
    if (!subTaskName.trim() || !personName) {
      toast.error("Please fill in both Sub Task name and select a member.");
      return;
    }

    try {
      const resultAction = await dispatch(
        addSubTaskMember({
          Data: subTaskName.trim(),
          member: [personName],
        })
      );

      if (addSubTaskMember.fulfilled.match(resultAction)) {
        toast.success("Sub Task added successfully!", {
          position: "top-right",
          autoClose: 3000,
        });

        setSubTaskName("");
        setPersonName("");

        dispatch(ShowAllSubTask());
      } else {
        toast.error(resultAction.payload || "Failed to add Sub Task.", {
          position: "top-right",
          autoClose: 3000,
        });
      }
    } catch (error) {
      toast.error("Something went wrong while adding the task.", {
        position: "top-right",
        autoClose: 3000,
      });
    }
  };

  const handleChange = (event) => {
    const {
      target: { value },
    } = event;
    const selectedNames = typeof value === "string" ? value.split(",") : value;
    setPersonName(selectedNames);
  };

  const handleClear = (e) => {
    e.stopPropagation();
    setPersonName("");
  };

  const names = useMemo(() => {
    return Array.isArray(ActiveMemberData?.data)
      ? ActiveMemberData.data
          .map((el) => ({
            value: el._id,
            label: el.Name,
          }))
          .sort((a, b) => (a.label || "").localeCompare(b.label || ""))
      : [];
  }, [ActiveMemberData]);

  const rows = useMemo(() => {
    return (
      AllSubTaskData?.data?.map((el, index) => ({
        id: index + 1,
        MainId: el._id,
        SubTask: el.SubTaskName,
        Member: el.TeamId?.map((member) => member.Name).join(", ") || "",
      })) || []
    );
  }, [AllSubTaskData]);

  const columns = [
    {
      field: "id",
      headerName: "ID",
      width: 65,
      minWidth: 60,
      renderCell: (params) => (
        <span className="inline-flex items-center justify-center font-bold text-slate-700 bg-slate-100 border border-slate-200 px-2 py-0.5 rounded text-[11px]">
          #{params.value}
        </span>
      ),
    },
    {
      field: "SubTask",
      headerName: "SUB TASK NAME",
      minWidth: 220,
      renderCell: (params) => (
        <div className="flex items-center gap-2 min-w-0">
          <div className="w-7 h-7 rounded-md bg-blue-50 border border-blue-100 flex items-center justify-center text-[#133A69] shrink-0 font-bold text-[11px]">
            <Layers size={13} />
          </div>
          <span className="font-semibold text-slate-800 truncate" title={params.value}>
            {params.value || "--"}
          </span>
        </div>
      ),
    },
    {
      field: "Member",
      headerName: "ASSIGNED MEMBERS",
      minWidth: 240,
      renderCell: (params) => {
        if (!params.value) {
          return <span className="text-slate-400 text-xs italic">No members assigned</span>;
        }
        const memberList = params.value.split(", ");
        return (
          <div className="flex flex-wrap gap-1 py-1">
            {memberList.map((m, idx) => (
              <span
                key={idx}
                className="inline-flex items-center gap-1 px-2 py-0.5 text-[11px] font-semibold bg-slate-100 text-slate-700 border border-slate-200 rounded-md"
              >
                <User size={10} className="text-slate-400 shrink-0" />
                <span className="truncate max-w-[140px]">{m}</span>
              </span>
            ))}
          </div>
        );
      },
    },
    {
      field: "showTask",
      headerName: "ACTION",
      width: 100,
      minWidth: 100,
      renderCell: (params) => (
        <button
          type="button"
          className="action-btn action-btn-show shadow-xs"
          onClick={() => handlePage(params.row.MainId)}
          title="Edit Sub Task"
        >
          <Pencil size={12} />
          <span>Edit</span>
        </button>
      ),
    },
  ];

  if ((!AllSubTaskData?.data || !ActiveMemberData?.data) && loading) {
    return <Loader />;
  }

  return (
    <div className="w-full flex flex-col space-y-4 py-2">
      {/* Compact Header Banner */}
      <div className="page-header-banner-compact">
        <div>
          <h4>Sub Task Master</h4>
          <span className="page-subtitle">
            Configure sub-tasks and assign designated members
          </span>
        </div>
        <span className="px-2.5 py-1 text-xs font-semibold text-white bg-white/20 border border-white/30 rounded-lg">
          Total Sub Tasks: {rows.length}
        </span>
      </div>

      {/* Create Sub Task Form Card */}
      <div className="bg-white border border-slate-200 rounded-xl shadow-xs overflow-hidden">
        <div className="px-4 py-3 bg-slate-50/80 border-b border-slate-200 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className="w-2 h-2 rounded-full bg-[#133A69]"></span>
            <span className="font-bold text-sm text-slate-800">Create New Sub Task</span>
          </div>
          <span className="text-xs text-slate-500 font-medium">
            Fill the fields below to add a new sub-task definition
          </span>
        </div>

        <div className="p-4">
          <div className="grid grid-cols-12 gap-4">
            {/* Task Name Input */}
            <div className="col-span-12 md:col-span-6">
              <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-1.5">
                Sub Task Name <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <input
                  type="text"
                  placeholder="Enter Sub Task Name (e.g. Frontend Design, API Integration)"
                  value={subTaskName}
                  onChange={(e) => setSubTaskName(e.target.value)}
                  className="w-full pl-9 pr-3 py-2 text-xs bg-slate-50 border border-slate-300 rounded-lg focus:bg-white focus:border-[#133A69] focus:ring-2 focus:ring-blue-500/20 outline-none transition-all placeholder:text-slate-400 font-medium shadow-xs"
                />
                <Layers
                  size={14}
                  className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none"
                />
              </div>
            </div>

            {/* Member Select */}
            <div className="col-span-12 md:col-span-6">
              <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-1.5">
                Select Member <span className="text-red-500">*</span>
              </label>
              <Select
                value={personName}
                onChange={handleChange}
                displayEmpty
                fullWidth
                IconComponent={() => (
                  <ChevronDown size={15} className="mr-2 text-slate-400 pointer-events-none" />
                )}
                MenuProps={{
                  disablePortal: true,
                  PaperProps: {
                    sx: {
                      maxHeight: 320,
                      mt: 0.5,
                      borderRadius: "10px",
                      boxShadow: "0 10px 25px -5px rgba(0,0,0,0.1), 0 8px 10px -6px rgba(0,0,0,0.1)",
                      border: "1px solid #e2e8f0",
                      "& .MuiMenuItem-root": {
                        fontSize: "0.8rem",
                        padding: "8px 14px",
                        "&:hover": {
                          backgroundColor: "#f1f5f9",
                        },
                        "&.Mui-selected": {
                          backgroundColor: "#e0f2fe",
                          fontWeight: 600,
                          color: "#0369a1",
                        },
                      },
                    },
                  },
                  anchorOrigin: {
                    vertical: "bottom",
                    horizontal: "left",
                  },
                  transformOrigin: {
                    vertical: "top",
                    horizontal: "left",
                  },
                }}
                sx={{
                  backgroundColor: "#f8fafc",
                  borderRadius: "8px",
                  fontSize: "0.75rem",
                  "& .MuiOutlinedInput-notchedOutline": {
                    borderColor: "#cbd5e1",
                  },
                  "&:hover .MuiOutlinedInput-notchedOutline": {
                    borderColor: "#94a3b8",
                  },
                  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                    borderColor: "#133A69",
                    borderWidth: "1.5px",
                  },
                  "& .MuiSelect-select": {
                    padding: "7.5px 12px",
                    display: "flex",
                    alignItems: "center",
                  },
                }}
                endAdornment={
                  personName ? (
                    <InputAdornment position="end" sx={{ mr: 2 }}>
                      <IconButton size="small" onClick={handleClear} edge="end">
                        <X size={14} className="text-slate-400 hover:text-slate-600" />
                      </IconButton>
                    </InputAdornment>
                  ) : null
                }
              >
                <MenuItem value="" disabled>
                  <span className="text-slate-400">Choose team member...</span>
                </MenuItem>
                {names.length > 0 ? (
                  names.map((name) => (
                    <MenuItem key={name.value} value={name.value}>
                      <div className="flex items-center gap-2">
                        <User size={12} className="text-slate-400" />
                        <span>{name.label}</span>
                      </div>
                    </MenuItem>
                  ))
                ) : (
                  <MenuItem disabled>No members available</MenuItem>
                )}
              </Select>
            </div>

            {/* Submit Button */}
            <div className="col-span-12 flex justify-end pt-2">
              <button
                type="button"
                onClick={handleTaskSubmit}
                className="inline-flex items-center gap-2 px-5 py-2 text-xs font-semibold text-white bg-[#133A69] hover:bg-[#0d284a] active:bg-[#081b33] rounded-lg shadow-xs hover:shadow transition-all"
              >
                <PlusCircle size={14} />
                <span>Submit Sub Task</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Directory Table Card */}
      <div className="inhouse-card">
        <div
          className="inhouse-card-header"
          style={{ borderLeft: "4px solid #133A69" }}
        >
          <div className="flex items-center gap-2">
            <span className="px-2.5 py-1 text-white font-semibold text-xs rounded-md bg-[#133A69]">
              Sub Task Directory
            </span>
            <span className="text-slate-500 text-xs font-medium">
              List of all registered sub-tasks and designated members
            </span>
          </div>
          <span className="px-2.5 py-1 text-xs font-semibold text-slate-700 bg-white border border-slate-200 rounded-md shadow-xs">
            Total: {rows.length}
          </span>
        </div>
        <div className="inhouse-card-body">
          <SupportStyleTable
            tableId="subtask_master_table"
            fileName="SubTask_Master_List"
            columns={columns}
            rows={rows}
            fullHeight={false}
            emptyComponent="No sub tasks defined yet."
          />
        </div>
      </div>
    </div>
  );
};

export default SubTask;
